/**
 * Mounts analytics once, inside BrowserRouter and AuthContextProvider.
 *
 * Responsibilities:
 *   1. register the app's route patterns so URLs can be collapsed to
 *      `/operator/order/:id` before anything is sent
 *   2. send a pageview on every route change
 *   3. identify the signed-in user by uid + role, and reset on sign-out
 *
 * Renders children untouched. If REACT_APP_POSTHOG_KEY is unset every call
 * below is a no-op.
 */
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { customerRoutes, publicRoutes, staffRoutes } from '../routes';
import { useAuthState } from '../utils/firebase';
import { analyticsEnabled, capturePageview, identifyUser, initAnalytics, resetAnalytics, setRoutePatterns } from '../utils/analytics';

// routes.ts is already the source of truth for these patterns, so this stays in
// sync automatically when routes are added or removed.
const ALL_ROUTE_PATTERNS = [...staffRoutes, ...customerRoutes, ...publicRoutes]
  .map((route) => route.path)
  // Longest first so `/operator/order/:id` wins over a shorter prefix match.
  .sort((a, b) => b.length - a.length);

export const AnalyticsProvider: React.FC = ({ children }) => {
  const location = useLocation();
  const auth = useAuthState();
  const identifiedUid = useRef<string | null>(null);

  useEffect(() => {
    if (!analyticsEnabled()) return;
    setRoutePatterns(ALL_ROUTE_PATTERNS);
    initAnalytics();
  }, []);

  useEffect(() => {
    if (!analyticsEnabled()) return;
    capturePageview(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!analyticsEnabled()) return;

    const uid = auth.user?.uid;
    if (uid && identifiedUid.current !== uid) {
      identifiedUid.current = uid;
      identifyUser(uid, {
        roles: auth.firestoreUser?.roles,
        isAdmin: auth.firestoreUser?.isAdmin,
        isReadOnlyRole: auth.firestoreUser?.isReadOnlyRole,
      });
    }

    // Signed out: reset so the next user on a shared machine is a new person.
    if (!uid && identifiedUid.current) {
      identifiedUid.current = null;
      resetAnalytics();
    }
  }, [auth.user, auth.firestoreUser]);

  return <>{children}</>;
};

export default AnalyticsProvider;
